import { type ReactNode } from 'react';
import { cn } from '@/client/lib/utils';

// Branded empty state used across public, portal, and admin lists. Keeps the
// mascot voice consistent. Compact by default; pass variant="page" for big
// pages where the empty state IS the page (e.g. Portal courses with no
// enrollments yet).
export type EmptyStateProps = {
  title: string;
  description?: string;
  /** "compact" (inline glass card) or "page" (centred, larger glass surface) */
  variant?: 'compact' | 'page';
  /** Lucide icon or any node. Falls back to the brand mascot image. */
  icon?: ReactNode;
  /** Optional CTA shown below the description. */
  action?: ReactNode;
  className?: string;
};

export function EmptyState({
  title,
  description,
  variant = 'compact',
  icon,
  action,
  className,
}: EmptyStateProps) {
  const isPage = variant === 'page';
  return (
    <div
      className={cn(
        'dr-card-static text-center animate-fade-up',
        isPage ? 'p-12 sm:p-16' : 'p-8 sm:p-10',
        className,
      )}
    >
      <div className="mx-auto flex justify-center">
        {icon ? (
          <span
            className={cn(
              'inline-flex items-center justify-center rounded-2xl',
              'bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary',
              isPage ? 'h-16 w-16' : 'h-14 w-14',
            )}
          >
            {icon}
          </span>
        ) : (
          <img
            src="/brand/mascot-purple.svg"
            alt=""
            className={isPage ? 'h-24 w-auto opacity-90 animate-float' : 'h-16 w-auto opacity-85'}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
      </div>
      <h2
        className={cn(
          'font-display text-primary-dark',
          isPage ? 'mt-6 text-h2' : 'mt-5 text-h3',
        )}
      >
        {title}
      </h2>
      {description && (
        <p
          className={cn(
            'mx-auto mt-3 font-sans text-text-secondary leading-relaxed',
            isPage ? 'max-w-md text-body-large' : 'max-w-sm text-body',
          )}
        >
          {description}
        </p>
      )}
      {action && <div className="mt-7 flex justify-center">{action}</div>}
    </div>
  );
}
